import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/firebaseConfig";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { useRecoilState } from "recoil";
import { usernameState, userTypeState } from "../../Recoil/atoms";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const SignUpPopUp = ({ showPopUp, onClose }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useRecoilState(usernameState);
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role] = useState("user"); // Set default role as "user"
  const [userType, setUserType] = useRecoilState(userTypeState);

  const [errors, setErrors] = useState({
    username: "",
    email: "",
    password: "",
  });

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "username":
        if (!value.trim()) error = "Username is required";
        else if (value.includes(" ")) error = "Username cannot contain spaces";
        else if (/[^a-zA-Z0-9._]/.test(value))
          error = "Invalid characters in username";
        break;
      case "email":
        if (!value) error = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(value)) error = "Invalid email format";
        break;
      case "password":
        if (!value) error = "Password is required";
        else if (value.length < 6)
          error = "Password must be at least 6 characters";
        else if (!/[A-Z]/.test(value))
          error = "Include at least one uppercase letter";
        else if (!/[0-9]/.test(value)) error = "Include at least one number";
        break;
      default:
        break;
    }
    return error;
  };

  const handleInputChange = (field, value) => {
    if (field === "username") setUsername(value);
    if (field === "email") setEmail(value);
    if (field === "password") setPassword(value);

    setErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  };

  const checkUsernameAvailability = async (username) => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("username", "==", username));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const storeUserInDB = async (user, uid) => {
    try {
      console.log("The value of the user field is ....", uid);
      const payload = {
        uid: uid,
        username: username,
        email: user.email,
        role: "user",
      };

      const response = await fetch(
        "https://dance-ai-app.azurewebsites.net/api/store_user",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        console.error("Error storing user in database:", result.error);
      } else {
        console.log("✅ User successfully stored in database:", result);
      }
    } catch (error) {
      console.error("❌ API error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const usernameError = validateField("username", username);
    const emailError = validateField("email", email);
    const passwordError = validateField("password", password);

    setErrors({
      username: usernameError,
      email: emailError,
      password: passwordError,
    });

    if (usernameError || emailError || passwordError) return;

    const usernameExists = await checkUsernameAvailability(username);
    if (usernameExists) {
      setErrors((prev) => ({ ...prev, username: "Username already taken" }));
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (!user) {
        console.error("❌ User creation failed, user object is null");
        return;
      }

      const uid = user.uid;
      if (!uid) {
        console.error("❌ UID is undefined");
        return;
      }

      await updateProfile(user, { displayName: username });
      setUserId(uid);
      setUsername(username);
      setUserType("user");

      await addDoc(collection(db, "users"), {
        uid: uid,
        username,
        role: "user",
      });
      await storeUserInDB(user, uid);

      navigate("/home");
      console.log("User signed up successfully!");
      onClose();
    } catch (error) {
      console.error("Error during signup:", error);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50`}
    >
      <div className="bg-white rounded-lg p-8 shadow-md relative w-96">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close signup popup"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl font-bold mb-6">Sign Up</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              className="w-full border p-2 rounded"
            />
            {errors.username && (
              <p className="text-red-500 text-sm mt-1">{errors.username}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="w-full border p-2 rounded"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              className="w-full border p-2 rounded"
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-indigo-500 text-white py-2 rounded-lg"
          >
            Sign Up
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUpPopUp;
